import { useState, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { useImportSprints } from '@/hooks/useSprints';
import { parseSprintCSV, sprintDurationDays } from '@/utils/csvParsing';
import type { ParsedSprintRow } from '@/utils/csvParsing';

type ImportState = 'idle' | 'parsing' | 'preview' | 'loading' | 'success' | 'error';

interface SprintImportProps {
  onImported?: (count: number) => void;
  onViewTimeline?: () => void;
}

export function SprintImport({ onImported, onViewTimeline }: SprintImportProps) {
  const [importState, setImportState] = useState<ImportState>('idle');
  const [preview, setPreview] = useState<ParsedSprintRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: importSprints } = useImportSprints();

  const handleFile = useCallback(async (file: File) => {
    setParseErrors([]);
    setImportError(null);
    setPreview([]);
    setImportState('parsing');

    try {
      const rows = await parseSprintCSV(file);
      setPreview(rows);
      setImportState('preview');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to parse CSV';
      setParseErrors([msg]);
      setImportState('error');
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDropZoneKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleImport = () => {
    if (preview.length === 0) return;
    setImportState('loading');
    setImportError(null);

    importSprints(preview, {
      onSuccess: () => {
        setImportedCount(preview.length);
        setImportState('success');
        onImported?.(preview.length);
      },
      onError: (err) => {
        setImportError(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setImportState('preview');
      },
    });
  };

  const handleReset = () => {
    setImportState('idle');
    setPreview([]);
    setParseErrors([]);
    setImportError(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Import Sprint Calendar</h3>
        <p className="mt-1 text-sm text-amber-800">
          ⚠️ Importing a CSV replaces your existing sprint calendar entirely.
        </p>
      </div>

      {/* ── Drop zone (AC1, AC10) ── */}
      {(importState === 'idle' || importState === 'error') && (
        <div
          role="button"
          tabIndex={0}
          aria-label="File upload drop zone — click or drag a CSV file here"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={handleDropZoneKeyDown}
          className={[
            'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500',
            isDragOver
              ? 'border-blue-400 bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50',
          ].join(' ')}
        >
          <svg className="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M12 16v-8m0 0-3 3m3-3 3 3M4 16v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1" />
          </svg>
          <p className="text-sm text-gray-600">
            <span className="font-medium text-blue-600">Browse for a CSV file</span>
            {' '}or drag and drop it here
          </p>
          <p className="text-xs text-gray-600">Only .csv files are accepted</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="sr-only"
            aria-label="Upload CSV file"
            tabIndex={-1}
            onChange={handleFileInputChange}
          />
        </div>
      )}

      {/* ── Parsing indicator ── */}
      {importState === 'parsing' && (
        <div role="status" aria-label="Parsing CSV" className="flex items-center gap-2 text-sm text-gray-600 py-4">
          <svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
          </svg>
          Reading CSV…
        </div>
      )}

      {/* ── Parse errors (AC2, AC6, AC14) ── */}
      {parseErrors.length > 0 && (
        <div role="alert" className="rounded-md bg-red-50 border border-red-200 p-3 space-y-1">
          {parseErrors.map((err, i) => (
            <p key={i} className="text-sm text-red-700">{err}</p>
          ))}
          <button
            type="button"
            onClick={handleReset}
            className="mt-1 text-xs text-red-600 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* ── Import error ── */}
      {importError && (
        <div role="alert" className="rounded-md bg-red-50 border border-red-200 p-3">
          <p className="text-sm text-red-700">{importError}</p>
        </div>
      )}

      {/* ── Preview table (AC5, AC11) ── */}
      {(importState === 'preview' || importState === 'loading') && preview.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm text-gray-700 font-medium">
            Preview — {preview.length} sprint{preview.length !== 1 ? 's' : ''} found
          </p>
          <div className="overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Sprint Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Start Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">End Date</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Duration (days)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {preview.map((sprint) => (
                  <tr key={sprint.sprint_number}>
                    <td className="px-3 py-2 text-gray-600">{sprint.sprint_number}</td>
                    <td className="px-3 py-2 text-gray-900">{sprint.name}</td>
                    <td className="px-3 py-2 text-gray-700">{format(sprint.start_date, 'MMM d, yyyy')}</td>
                    <td className="px-3 py-2 text-gray-700">{format(sprint.end_date, 'MMM d, yyyy')}</td>
                    <td className="px-3 py-2 text-gray-700">{sprintDurationDays(sprint.start_date, sprint.end_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Progress / import button (AC12) ── */}
          {importState === 'loading' ? (
            <div className="space-y-1" role="status" aria-label="Importing sprints">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
                </svg>
                Importing {preview.length} sprints…
              </div>
              <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full w-1/2 rounded-full bg-blue-500 animate-pulse" />
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleImport}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Import {preview.length} Sprint{preview.length !== 1 ? 's' : ''}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Success state (AC5, AC13) ── */}
      {importState === 'success' && (
        <div role="status" className="rounded-md bg-green-50 border border-green-200 p-4 flex items-start gap-3">
          <svg className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div className="space-y-2">
            <p className="text-sm font-medium text-green-800">
              {importedCount} sprint{importedCount !== 1 ? 's' : ''} imported successfully
            </p>
            <div className="flex gap-3">
              {onViewTimeline && (
                <button
                  type="button"
                  onClick={onViewTimeline}
                  className="text-sm font-medium text-green-700 underline"
                >
                  View on Timeline
                </button>
              )}
              <button
                type="button"
                onClick={handleReset}
                className="text-sm text-green-700 underline"
              >
                Import another file
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Format help (AC4, AC14) ── */}
      <details className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
        <summary className="cursor-pointer font-medium text-gray-700 select-none">CSV format reference</summary>
        <div className="mt-3 space-y-2">
          <p className="text-gray-600">Required columns (flexible naming):</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li><code className="text-xs bg-gray-100 px-1 rounded">sprint_name</code> or <code className="text-xs bg-gray-100 px-1 rounded">name</code> — Sprint name (optional, auto-generated if missing)</li>
            <li><code className="text-xs bg-gray-100 px-1 rounded">start_date</code> or <code className="text-xs bg-gray-100 px-1 rounded">start</code> — Sprint start date</li>
            <li><code className="text-xs bg-gray-100 px-1 rounded">end_date</code> or <code className="text-xs bg-gray-100 px-1 rounded">end</code> — Sprint end date</li>
          </ul>
          <p className="text-gray-600 mt-2">Accepted date formats: <code className="text-xs bg-gray-100 px-1 rounded">YYYY-MM-DD</code>, <code className="text-xs bg-gray-100 px-1 rounded">MM/DD/YYYY</code>, <code className="text-xs bg-gray-100 px-1 rounded">DD-MM-YYYY</code> <span className="text-xs text-gray-600">(day must be &gt; 12)</span></p>
          <pre className="mt-2 rounded bg-gray-100 p-2 text-xs text-gray-700 overflow-auto">{`sprint_number,sprint_name,start_date,end_date
1,Sprint 1,2025-01-06,2025-01-19
2,Sprint 2,2025-01-20,2025-02-02
3,Sprint 3,2025-02-03,2025-02-16`}</pre>
        </div>
      </details>
    </div>
  );
}
